import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { RentalLeaseType, RentalLeaseTypeOptions } from '../LoanConstants';
import DatePicker from 'react-datepicker';
import { NumericFormat } from 'react-number-format';
import { addCalendarYear } from '../../../common/SharedFunctions';

export const RentalUnitLeaseDialog = ({ lease, onSubmit, onCancel }) => {
    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
    };

    const [leaseTypeId, set_leaseTypeId] = useState(RentalLeaseType.Annual);
    const [monthlyRent, set_monthlyRent] = useState(null);
    const [startDate, set_startDate] = useState(null);
    const [endDate, set_endDate] = useState(null);
    const [notes, set_notes] = useState('');

    useEffect(() => {
        if (!lease) {
            resetState();
            return;
        }
        set_leaseTypeId(lease.leaseTypeId);
        set_monthlyRent(lease.monthlyRent);
        const sd = lease.startDate ? new Date(lease.startDate) : lease.minDate ? new Date(lease.minDate) : null;
        set_startDate(sd);
        const ed = lease.endDate ? new Date(lease.endDate) : sd ? addCalendarYear(sd) : null;
        set_endDate(ed);
        set_notes(lease.notes);
    }, [lease]);

    const resetState = () => {
        set_leaseTypeId(RentalLeaseType.Annual);
        set_monthlyRent(null);
        set_startDate(null);
        set_endDate(null);
        set_notes('');
    };

    const _onCancel = () => {
        onCancel && onCancel();
    };

    const _onSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const model = {
            id: lease.id,
            unitGuid: lease.unitGuid,
            guid: lease.guid,
            leaseTypeId,
            monthlyRent,
            startDate,
            endDate,
            notes,
        };

        onSubmit && onSubmit(model);
    };

    const setLeaseTypeId = (value) => {
        set_leaseTypeId(value);
    };

    const setMonthlyRent = (value) => {
        set_monthlyRent(value);
    };

    const setStartDate = (value) => {
        if (value) {
            const dt = new Date(value);
            set_startDate(dt);
            if (!endDate) {
                set_endDate(addCalendarYear(dt));
            }
        }
    };

    const setEndDate = (value) => {
        set_endDate(value ? new Date(value) : null);
    };

    const setNotes = (value) => {
        set_notes(value);
    };

    if (!lease) {
        return '';
    }

    return (
        <Dialog
            maxWidth='sm'
            open={true}
            onClose={_onCancel}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex={-1}
                        role='dialog'
                        style={{ width: '750px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1 style={{ whiteSpace: 'nowrap' }}>Unit Lease</h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={_onCancel}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                        </div>

                        <form
                            name='unitLeaseForm'
                            onSubmit={(event) => _onSubmit(event)}
                        >
                            <div
                                className='standard-form no-search'
                                style={{
                                    padding: '0',
                                    margin: '10px 0px',
                                    height: '300px',
                                }}
                            >
                                <div style={{ padding: '0px 7px' }}>
                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor={'leaseTypeId'}>
                                                Lease Type <span className='req'>*</span>
                                            </label>
                                            <select
                                                id={'leaseTypeId'}
                                                value={leaseTypeId}
                                                onChange={(event) => setLeaseTypeId(parseInt(event.target.value))}
                                                required={true}
                                            >
                                                {RentalLeaseTypeOptions.map((fs) => {
                                                    return (
                                                        <option
                                                            key={fs.id}
                                                            value={fs.id}
                                                        >
                                                            {fs.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor={'monthlyRent'}>
                                                Mnthly Rent <span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                thousandSeparator=','
                                                prefix='$'
                                                id={'monthlyRent'}
                                                name={'monthlyRent'}
                                                placeholder='$'
                                                value={monthlyRent}
                                                decimalScale={2}
                                                allowNegative={false}
                                                displayType='input'
                                                required={true}
                                                onValueChange={(values) => {
                                                    setMonthlyRent(values.floatValue);
                                                }}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value > 0;
                                                }}
                                            />
                                        </div>
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor={'startDate'}>
                                                Start Date <span className='req'>*</span>
                                            </label>
                                            <DatePicker
                                                id={'startDate'}
                                                selected={startDate}
                                                onChange={(value) => setStartDate(value)}
                                                required={true}
                                                showYearDropdown
                                                minDate={lease.minDate}
                                            />
                                        </div>
                                        <div className='columns small-12 large-3'>
                                            <label htmlFor={'endDate'}>
                                                End Date <span className='req'>*</span>
                                            </label>
                                            <DatePicker
                                                id={'endDate'}
                                                selected={endDate}
                                                onChange={(value) => setEndDate(value)}
                                                required={true}
                                                showYearDropdown
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div className='columns small-12 large-12'>
                                            <label htmlFor={'notes'}>Notes</label>
                                            <textarea
                                                value={notes}
                                                style={{ width: '100%' }}
                                                rows={4}
                                                id={'notes'}
                                                onChange={(e) => setNotes(e.target.value)}
                                                required={false}
                                                placeholder=''
                                                maxLength={2000}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className='button-footer'
                                style={styles.buttonContainer}
                            >
                                <button
                                    style={styles.buttonMarginRight}
                                    type='submit'
                                    className='button primary'
                                >
                                    Update
                                </button>
                                <button
                                    type='button'
                                    className='button hollow'
                                    data-dismiss='modal'
                                    onClick={_onCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
